/**
 * The core unit. Four variants from the design:
 *   standard  4:5 grid tile  ·  featured  3:2 hero  ·  mini  1:1 rail  ·  bare  media only
 *
 * Media switches on prompt type: image/video get artwork, text/code a monospace excerpt,
 * audio a waveform. That mirrors the design and, more usefully, means a text prompt with no
 * example image still reads as content rather than as a broken tile.
 */

import type { PromptCard as PromptCardDto, PromptType } from '@promptvault/shared';
import { compactNumber, decimal1, hashString } from '../lib/format';
import { promptTypeLabel, useI18n } from '../lib/i18n';
import { haptic } from '../hooks/useTg';
import { Icon } from './Icon';
import { Media } from './Media';
import {
  MEANINGFUL_COUNT,
  MEANINGFUL_RATINGS,
  SourceStatLine,
  hasInternalSignal,
} from './SourceStats';

export type CardVariant = 'standard' | 'featured' | 'mini' | 'bare';

const VARIANTS: Record<
  CardVariant,
  { ratio: string; title: string; meta: boolean; badges: boolean; fav: boolean; body: boolean }
> = {
  standard: {
    ratio: '4 / 5',
    title: 'text-footnote font-semibold',
    meta: true,
    badges: true,
    fav: true,
    body: true,
  },
  featured: {
    ratio: '3 / 2',
    title: 'text-headline font-semibold',
    meta: true,
    badges: true,
    fav: true,
    body: true,
  },
  mini: {
    ratio: '1 / 1',
    title: 'text-caption font-medium',
    meta: false,
    badges: false,
    fav: false,
    body: true,
  },
  bare: {
    ratio: '4 / 5',
    title: 'text-footnote font-semibold',
    meta: false,
    badges: false,
    fav: true,
    body: false,
  },
};

const TEXTUAL: PromptType[] = ['text', 'code', 'agent'];

function Waveform({ seed }: { seed: string }) {
  // Deterministic bars, so the same prompt always draws the same waveform.
  const h = hashString(seed);
  return (
    <div className="absolute inset-0 flex items-center justify-center gap-[2.5px] bg-media-bg px-3">
      {Array.from({ length: 22 }, (_, i) => {
        const height = 14 + Math.abs(Math.sin((h % 97) + i * 1.7)) * 52;
        return (
          <span
            key={i}
            className={`w-[2.5px] flex-none rounded-full ${
              i % 3 === 0 ? 'bg-star' : 'bg-on-media/60'
            }`}
            style={{ height: `${height.toFixed(0)}%` }}
          />
        );
      })}
    </div>
  );
}

export interface PromptCardProps {
  card: PromptCardDto;
  variant?: CardVariant;
  onOpen: (card: PromptCardDto) => void;
  /** Omit to hide the favourite affordance (rails, where space is tight). */
  onToggleFavorite?: (card: PromptCardDto) => void;
  favorited?: boolean;
  priority?: boolean;
}

export function PromptCard({
  card,
  variant = 'standard',
  onOpen,
  onToggleFavorite,
  favorited = false,
  priority = false,
}: PromptCardProps) {
  const { t, lang } = useI18n();
  const cfg = VARIANTS[variant];
  const isText = TEXTUAL.includes(card.type);
  const isAudio = card.type === 'audio' || card.type === 'music';
  const showFav = cfg.fav && Boolean(onToggleFavorite);

  const badges =
    card.models.length > 2
      ? [
          card.models[0]!.name,
          card.models[1]!.name,
          `+${compactNumber(card.models.length - 2, lang)}`,
        ]
      : card.models.map((m) => m.name);

  return (
    <button
      type="button"
      /* A stable hook for the e2e harnesses. They previously selected on `a[href*="/p/"]`, which
         has never been the markup — the card is a button that routes — so every harness silently
         counted zero cards and reported a working screen as empty. */
      data-testid="prompt-card"
      onClick={() => {
        haptic('light');
        onOpen(card);
      }}
      className="flex w-full select-none flex-col gap-2 rounded-card bg-surface p-2 text-start transition-transform duration-fast ease-pv active:scale-[0.972]"
    >
      <div className="relative w-full overflow-hidden rounded-thumb">
        {isText ? (
          <div
            className="relative flex flex-col justify-between gap-1.5 bg-fill p-2.5"
            style={{ aspectRatio: cfg.ratio }}
          >
            <p dir="ltr" className="clamp-3 font-mono text-caption2 leading-relaxed text-hint">
              {card.title}
            </p>
            <span className="flex-none self-start rounded-[6px] bg-accent-tint px-1.5 py-[3px] text-[9px] font-semibold uppercase tracking-wider text-accent">
              {/* `promptTypeLabel`, not a literal: this said `'Code'` in English on a Farsi card
                  sitting directly beside one badged `پرامپت`, in the same grid. The map that
                  already knows the word in both languages is one import away. */}
              {card.type === 'code' ? promptTypeLabel('code', lang) : t.prompt}
            </span>
          </div>
        ) : isAudio ? (
          <div className="relative" style={{ aspectRatio: cfg.ratio }}>
            <Waveform seed={card.id} />
          </div>
        ) : (
          <Media
            seed={card.id}
            example={card.firstExample}
            ratio={cfg.ratio}
            alt={card.title}
            // Blurred unconditionally on the prompt's own flag. Whether an NSFW prompt reaches
            // the grid at all is decided server-side by the user's nsfwVisible setting; this is
            // the second gate, so a deep link or a stale cache can't surface it unprompted (D15).
            //
            // Deliberately no `onReveal`. A card is one tap target and that tap opens the
            // prompt; the reveal lives on the detail screen, where `Gallery` owns the state and
            // the gesture is not competing with a navigation. Passing `blurred` without a
            // handler used to render a "Tap to reveal" button that could only navigate.
            blurred={card.isNsfw}
            priority={priority}
          />
        )}

        {/*
          No play badge and no duration pill here.

          `Media` draws both, from `firstExample.mediaType` — the type of the file we actually
          hold. This block drew a second pair from `card.type`, so every video tile carried two
          play glyphs, and would have carried two durations the moment any example gained one.
          The chrome that sits on media belongs to the component that draws the media.
        */}

        {showFav && (
          <span
            role="button"
            tabIndex={0}
            aria-label={favorited ? t.saved : t.save}
            onClick={(e) => {
              e.stopPropagation();
              haptic('medium');
              onToggleFavorite?.(card);
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' && e.key !== ' ') return;
              e.preventDefault();
              e.stopPropagation();
              haptic('medium');
              onToggleFavorite?.(card);
            }}
            className="absolute end-0 top-0 flex h-11 w-11 cursor-pointer items-start justify-end p-1.5"
          >
            <span
              className={`flex h-[30px] w-[30px] items-center justify-center rounded-full transition-[background,transform] duration-200 ease-pv ${
                favorited ? 'scale-105 bg-surface text-destructive' : 'text-on-media'
              }`}
              style={favorited ? undefined : { background: 'var(--pv-media-scrim)' }}
            >
              <Icon name="heart" size={15} fill={favorited} />
            </span>
          </span>
        )}
      </div>

      {cfg.body && (
        <div className="flex flex-col gap-1.5 px-0.5 pb-0.5">
          <span className={`bidi-plaintext clamp-2 text-text ${cfg.title}`}>{card.title}</span>

          {cfg.badges && badges.length > 0 && (
            <span className="flex gap-1 overflow-hidden">
              {badges.map((b) => (
                <span
                  key={b}
                  className="flex-none whitespace-nowrap rounded-thumb bg-accent-tint px-1.5 py-[2.5px] text-caption2 font-medium text-accent"
                >
                  {b}
                </span>
              ))}
            </span>
          )}

          {/*
            The meta line used to render `★ 0.0 · ⬇ 0` on every tile, unconditionally, from our
            own counters — which are zero across essentially the whole library. A prompt with
            2,913 Civitai reactions was being shown as if nobody had ever wanted it.

            Now the source's number leads, because for this library it is the only real signal of
            quality there is. Our own numbers appear underneath *only* once they mean something:
            a rating needs votes behind it, and a zero download count is a review rather than a
            fact.
          */}
          {cfg.meta && (
            <>
              <SourceStatLine engagement={card.sourceEngagement} />
              {hasInternalSignal(card) && (
                <span className="tnum flex items-center gap-1.5 text-caption text-hint">
                  {card.ratingCount >= MEANINGFUL_RATINGS && (
                    <>
                      <Icon name="star" size={11} fill className="text-star" />
                      {decimal1(card.ratingAvg, lang)}
                    </>
                  )}
                  {card.ratingCount >= MEANINGFUL_RATINGS &&
                    card.downloadCount >= MEANINGFUL_COUNT && (
                      <span aria-hidden="true" className="opacity-50">
                        ·
                      </span>
                    )}
                  {card.downloadCount >= MEANINGFUL_COUNT && (
                    <>
                      <Icon name="download" size={11} />
                      {compactNumber(card.downloadCount, lang)}
                    </>
                  )}
                </span>
              )}
            </>
          )}
        </div>
      )}
    </button>
  );
}
